/**
 * Root application component.
 *
 * Sets up React Query, routing, and the authentication lifecycle.
 * Routes are guarded by ProtectedRoute with role-based access control.
 */

import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/store/queryClient';
import { coreApiMode, landingPath } from '@/config/deployment';
import PlatformOrganizationsPage from '@/pages/platform/PlatformOrganizationsPage';
import PlatformOrganizationPage from '@/pages/platform/PlatformOrganizationPage';
import PlatformPlansPage from '@/pages/platform/PlatformPlansPage';
import PlatformAiUsagePage from '@/pages/platform/PlatformAiUsagePage';
import ConnectAiPage from '@/pages/ConnectAiPage';
import CoreProfilePage from '@/pages/CoreProfilePage';
import CoreOrgSettingsPage from '@/pages/CoreOrgSettingsPage';
import PciReadinessPage from '@/pages/PciReadinessPage';
import AuditPage from '@/pages/AuditPage';
import ActivationPage from '@/pages/ActivationPage';
import EnrollmentPage from '@/pages/EnrollmentPage';
import AuthCompletePage from '@/pages/AuthCompletePage';
import { useAuthStore } from '@/store/authStore';

import Layout from '@/components/common/Layout';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import LoginPage from '@/pages/LoginPage';
import AcceptInvitePage from '@/pages/AcceptInvitePage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DashboardPage from '@/pages/DashboardPage';
import AssetsPage from '@/pages/AssetsPage';
import ScansPage from '@/pages/ScansPage';
import FindingsPage from '@/pages/FindingsPage';
import ReportsPage from '@/pages/ReportsPage';
import AdminPage from '@/pages/AdminPage';
import OrgSettingsPage from '@/pages/OrgSettingsPage';
import ProfilePage from '@/pages/ProfilePage';
import DocsPage from '@/pages/DocsPage';
import LegalPage,{PoliciesPage,PoliciesIndex} from '@/pages/LegalPage';
import LegalAcceptanceGate from '@/components/legal/LegalAcceptanceGate';

export default function App() {
  const loadUser = useAuthStore((s) => s.loadUser);

  // Load the authenticated user on app initialization.
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  if (coreApiMode) return <QueryClientProvider client={queryClient}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/activate" element={<ActivationPage/>}/>
      <Route path="/enroll" element={<EnrollmentPage/>}/>
      <Route path="/auth/complete" element={<AuthCompletePage/>}/>
      <Route path="/accept-invite" element={<AcceptInvitePage/>}/>
      <Route path="/forgot-password" element={<ForgotPasswordPage/>}/>
      <Route path="/reset-password" element={<ResetPasswordPage/>}/>
      <Route path="/legal/:document" element={<LegalPage/>}/>
      <Route element={<ProtectedRoute><LegalAcceptanceGate><Layout /></LegalAcceptanceGate></ProtectedRoute>}>
        <Route path="/assets" element={<AssetsPage />} />
        <Route path="/scans" element={<ScansPage/>}/>
        <Route path="/findings" element={<FindingsPage/>}/>
        <Route path="/dashboard" element={<DashboardPage/>}/>
        <Route path="/reports" element={<ReportsPage/>}/>
        <Route path="/docs" element={<DocsPage/>}/>
        <Route path="/policies" element={<PoliciesIndex/>}/>
        <Route path="/policies/:document" element={<PoliciesPage/>}/>
        <Route path="/pci" element={<PciReadinessPage/>}/>
        <Route path="/audit" element={<ProtectedRoute allowedRoles={['platform_admin','customer_admin','remediation_lead']}><AuditPage/></ProtectedRoute>}/>
        <Route path="/profile" element={<CoreProfilePage />} />
        <Route path="/connect-ai" element={<ConnectAiPage />} />
        <Route path="/org-settings" element={<ProtectedRoute allowedRoles={['platform_admin','customer_admin']}><CoreOrgSettingsPage/></ProtectedRoute>}/>
        <Route path="/admin" element={<Navigate to="/platform/organizations" replace />} />
        <Route path="/platform/organizations" element={<ProtectedRoute allowedRoles={['platform_admin']}><PlatformOrganizationsPage /></ProtectedRoute>} />
        <Route path="/platform/organizations/:id" element={<ProtectedRoute allowedRoles={['platform_admin']}><PlatformOrganizationPage /></ProtectedRoute>} />
        <Route path="/platform/plans" element={<ProtectedRoute allowedRoles={['platform_admin']}><PlatformPlansPage /></ProtectedRoute>} />
        <Route path="/platform/ai-usage" element={<ProtectedRoute allowedRoles={['platform_admin']}><PlatformAiUsagePage /></ProtectedRoute>} />
      </Route>
      <Route path="*" element={<Navigate to={landingPath} replace />} />
    </Routes>
  </QueryClientProvider>;

  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected routes wrapped in the app shell layout */}
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/assets" element={<AssetsPage />} />
          <Route path="/scans" element={<ScansPage />} />
          <Route path="/findings" element={<FindingsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/docs" element={<DocsPage />} />

          {/* Platform admin only */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['platform_admin']}>
                <AdminPage />
              </ProtectedRoute>
            }
          />

          {/* Customer admin only */}
          <Route
            path="/org-settings"
            element={
              <ProtectedRoute allowedRoles={['customer_admin']}>
                <OrgSettingsPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Redirect root to dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </QueryClientProvider>
  );
}
